import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { EventsState } from '../../../../../store/types';
import Icon, { ICONS, COLORS } from '../../../../general-ui/icon';
import './video.scss';
import './moduleControls/controls.scss';
import { ColorKeys, LanguagesAbbr, SessionTypesEnum } from '../../../../../types/working-model';
import SessionStream from '../../../../live-event/session/session-stream/session-stream';
import EditSectionColorsModal, { EContentColorOverrideNames } from '../../../../general-ui/edit-section-colors/edit-section-colors-modal';
import { getSecondarySessionVideo, setVideoCustomizations } from '../../../../../store/actions/admin/create-event/session';
import BroadcastOptionsModal from '../../homepage/navigation/broadcast-options-modal';
import MainEventBannerBreakout from '../../../../live-event/modules/main-event-banner-breakout/main-event-banner-breakout';
import BreakoutBannerActions from './breakout-banner-actions';
import { OptionalComponent } from '../../../../../utils/optional-component';
import { getTemplateClassName } from '../../../../../utils/utils';


interface IVideoWrapper {
	template: string;
	styleOverrides: any;
	editorWrapperClass?: string;
	containerClass?: string;
}

export const VideoWrapper: React.FC<React.PropsWithChildren<IVideoWrapper>> = ({
	template,
	styleOverrides,
	editorWrapperClass = '',
	containerClass = '',
	children,
}) => {
	return (
		<div className={classNames("editor-wrapper broadcast", template, editorWrapperClass)} style={styleOverrides}>
			<div className={classNames("container", containerClass)}>
				{children}
			</div>
		</div>
	);
};

interface VideoProps {
	editorSize: string;
	language?: LanguagesAbbr;
	session_type?: SessionTypesEnum;
	interacted?: boolean;
	sessionHeaderRef: React.RefObject<HTMLDivElement>;
}

const Video: React.FC<VideoProps> = ({ editorSize, language, session_type, sessionHeaderRef }) => {
	const {
		CreateSessionReducer: {
			workingSession
		},
		CreateEventReducer: {
			workingEvent
		},
		AuthReducer: {
			token
		}
	} = useSelector((state: EventsState) => state);
	const [openStyleModal, setOpenStyleModal] = useState(false);
	const [optionsModalOpen, setOptionsModalOpen] = useState(false);

	const dispatch = useDispatch();

	const template = getTemplateClassName(workingEvent?.template.name);

	const handleFinishStyling = useCallback((colorKey: string) => {
		if (colorKey.startsWith('https://')) {
			dispatch(setVideoCustomizations({
				backgroundImage: colorKey,
				backgroundColor: undefined
			}));
		} else {
			dispatch(setVideoCustomizations({
				backgroundImage: undefined,
				backgroundColor: colorKey as ColorKeys
			}));
		}
	}, [dispatch]);

	const handleCancelStyling = useCallback(() => {
		setOpenStyleModal(false);
	}, []);

	const styleOverrides = useMemo(() => {
		if (workingSession?.video?.customizations?.backgroundImage) {
			return { backgroundImage: `url(${workingSession?.video?.customizations?.backgroundImage})` };
		}

		return {};
	}, [workingSession]);

	useEffect(() => {
		//get secondary session video if its been set
		if (workingSession?.session && token) {
			dispatch(getSecondarySessionVideo(workingSession.session, token));
		}
	}, [workingSession?.session, token, dispatch]);

	if (
		workingEvent
		&& workingSession
		&& workingSession.session_type === SessionTypesEnum.breakoutRooms
		// && mainEventBanner
	) {
		return (
			<VideoWrapper
				template={template}
				styleOverrides={styleOverrides}
				editorWrapperClass="breakout-editor-wrapper"
			>
				<BreakoutBannerActions session={workingSession} />
				<MainEventBannerBreakout
					template={template}
					session={workingSession}
				/>
			</VideoWrapper>
		);
	}

	return workingSession && (
		<>
			<VideoWrapper template={template} styleOverrides={styleOverrides} >
				{workingEvent && (
					<SessionStream
						playback={workingEvent.playbackUrls?.[`${workingSession.uuid}-${language}`]}
						secondaryVideos={workingEvent.secondaryVideos}
						session={workingSession}
						eventBundle={workingEvent}
						isEditor
						editorSize={editorSize}
						isAdmin={true}
						languageProp={language}
						sessionHeaderRef={sessionHeaderRef}
					/>
				)}
				<div className="video-actions">
					<button className="button round" onClick={() => setOpenStyleModal(true)}>
						<Icon
							name={ICONS.PAINTBRUSH}
							color={COLORS.WHITE}
							size={12}
							backgroundColor={COLORS.DARK_GREY}
						/>
					</button>
					<OptionalComponent display={session_type !== SessionTypesEnum.breakoutRooms}>
						<button className="button round" onClick={() => setOptionsModalOpen(true)}>
							<Icon
								name={ICONS.EDIT}
								color={COLORS.WHITE}
								size={12}
								backgroundColor={COLORS.DARK_GREY}
							/>
						</button>
					</OptionalComponent>
				</div>
			</VideoWrapper>

			<EditSectionColorsModal
				open={openStyleModal}
				title={EContentColorOverrideNames.background}
				backgroundImage={workingSession.image ?? undefined}
				onFinish={handleFinishStyling}
				onClose={handleCancelStyling}
				allowBackgroundImage={false}
			/>
			<BroadcastOptionsModal
				isOpen={optionsModalOpen}
				onClose={() => setOptionsModalOpen(false)}
			/>
		</>
	);
};

export default Video;
